import { activityStatus, contentTags } from "Constants/constants";
import studentRecordService from "Services/StudentRecordService";
import {uuidToHEX} from "Services/util";
import studentInformationService from "Services/StudentInformationService";
import {getUserData, setUserData} from "Services/API";
import { recommendationModes } from "../constants/constants";
import { getNextErssActivity } from "./erssService";
import { logSessionInfo } from "./logger";

const earlyReaderSequenceStart = "cb95adf1-b253-46ed-a3be-8ef2f344a660";
const spanishEarlyReaderSequenceStart = "2c4ae420-f668-11ec-b9fa-1f71bfc996aa";

export function getEarlyReaderMode(story = {}) {
  const tags = (story && story.tags && story.tags.length) ? story.tags : [];
  return {
    readWithMe: tags.includes(contentTags.EARLY_READER_READ_WITH_ME),
    wordList: tags.includes(contentTags.EARLY_READER_WORD_LIST),
    repetitiveText: tags.includes(contentTags.EARLY_READER_REPETITIVE_TEXT),
    changeOnePart: tags.includes(contentTags.EARLY_READER_CHANGE_ONE_PART),
    upAndDown: tags.includes(contentTags.EARLY_READER_UP_AND_DOWN),
    letterFlies: tags.includes(contentTags.EARLY_READER_LETTER_FLIES),
    elkoninBoxes: tags.includes(contentTags.EARLY_READER_ELKONIN_BOXES) || tags.includes(contentTags.EARLY_READER_ELKONIN),
  }
}

export function endOfEarlyReaderSequence(activity){
  if(!activity){
    return false;
  }
  const { status, story } = activity;
  const tags = (story.tags && story.tags.length) ? story.tags : [];
  return status === activityStatus.SCORED && tags.includes(contentTags.EARLY_READER_SEQUENCE_END)
}

export function clearEarlyReaderSequenceStart(locale){
  let earlyReaderSequenceStories = getUserData().sequenceStartStories;
  earlyReaderSequenceStories = earlyReaderSequenceStories.filter(element => element.locale !== locale);
  setUserData("earlyReaderSequenceStories",earlyReaderSequenceStories);
  if(earlyReaderSequenceStories.length === 0){
    setUserData("earlyReaderSequenceStart",false)
    setUserData("startingEarlyReaderSequence",false)
  }

}

const ERSS_LOCALE_MAP = { 'EN_US': 'en-us', 'ES_MX': 'es-mx' };

/**
 * Fetches the next early reader story(s) for a student from the ERSS service.
 * Returns null when the ERSS backend is unreachable (network error, timeout,
 * null/malformed response) so callers can distinguish "ERSS didn't answer"
 * from "ERSS answered." The caller in API.js skips the early_reader metadata
 * write when null, preventing a transient ERSS outage from permanently
 * demoting a real early reader. (TT-1420)
 *
 * Returns the nextStories array as-is when ERSS responds with a well-formed
 * payload — including [] when both early-reader flags are false (the normal
 * majority-of-logins path). The ERSS error field is logged but does not
 * control the return value; only a missing nextStories array triggers null.
 *
 * Sequence completion arrives as [{locale, storyId: null}], not []; the
 * caller's isEarlyReaderInSequence check handles that via the storyId guard.
 */
export async function getNextEarlyReaderStory(userId, isEarlyReader, isSpanishEarlyReader){
  try {
    let nextActivities = await getNextErssActivity(userId,isEarlyReader,isSpanishEarlyReader);
    if (!nextActivities || !Array.isArray(nextActivities.nextStories)) {
      logSessionInfo('erss_unavailable', {
        userId,
        reason: !nextActivities ? 'null_response' : 'missing_next_stories',
        error: nextActivities?.error,
      });
      return null;
    }
    return nextActivities.nextStories.map(s => ({
      ...s,
      locale: ERSS_LOCALE_MAP[s.locale] ?? s.locale,
    }));
  } catch (error) {
    logSessionInfo('erss_unavailable', {
      userId,
      reason: 'request_failed',
      error: error?.message ?? JSON.stringify(error?.errors ?? error),
    });
    return null;
  }
}

/**
 * Checks whether a given locale has a valid story in the ERSS sequence.
 * Guards against null/undefined/empty sequence — null signals ERSS was
 * unavailable (see getNextEarlyReaderStory). Sequence completion arrives
 * as [{locale, storyId: null}] and returns false via the storyId guard. (TT-1420)
 */
export function isEarlyReaderInSequence(sequence,locale){
  if (!sequence || !sequence.length) return false;
  const storyInSequence = sequence.find(story=>story.locale === locale);
  return (storyInSequence && storyInSequence.storyId) ? true : false;
}

export async function hasEarlyReaderSequenceInProgress(userId,locale='en-us'){
  const tutorActivities = await studentRecordService.getRecommendedStories(uuidToHEX(userId),0,"tutor",1,"",recommendationModes.SEQUENCE_LOCKED,locale);
  if(!tutorActivities.length){
    return false;
  }
  return tutorActivities.some(story => isEarlyReaderStory(story));
}

export function shouldFilterStoryFromReport(activity, scale) {
  const { storyTags } = activity;

  // return false if storyTags is null
  if (!storyTags) return false

  //Allow Word List and Repetitive Text if we are using ARM scale
  if(scale && scale == "ARM") {
    let returnValue = isEarlyReader(activity) && !storyTags.includes(contentTags.EARLY_READER_WORD_LIST) && !storyTags.includes(contentTags.EARLY_READER_REPETITIVE_TEXT) && activity.displayStatus != 'prereader';
    return returnValue;

  } else {
    let returnValue = isEarlyReader(activity);
    return returnValue;
  }
}

export function isEarlyReader(activity) {
  const { storyTags } = activity;

  // return false if storyTags is null
  if (!storyTags) return false

  return (storyTags.includes(contentTags.EARLY_READER_READ_WITH_ME) || storyTags.includes(contentTags.EARLY_READER_WORD_LIST) || storyTags.includes(contentTags.EARLY_READER_REPETITIVE_TEXT)  || storyTags.includes(contentTags.EARLY_READER_LETTER_FLIES) || storyTags.includes(contentTags.EARLY_READER_UP_AND_DOWN)  || storyTags.includes(contentTags.EARLY_READER_ELKONIN_BOXES) || storyTags.includes(contentTags.EARLY_READER_ELKONIN) || storyTags.includes(contentTags.EARLY_READER_CHANGE_ONE_PART) || activity.displayStatus === 'prereader');
}

export function isEarlyReaderStory(story) {
  if(!story) return false;

  const storyTags = story && story.tags;
  if (!storyTags) return false

  return (storyTags.includes(contentTags.EARLY_READER_READ_WITH_ME) || storyTags.includes(contentTags.EARLY_READER_WORD_LIST) || storyTags.includes(contentTags.EARLY_READER_REPETITIVE_TEXT)  || storyTags.includes(contentTags.EARLY_READER_LETTER_FLIES) || storyTags.includes(contentTags.EARLY_READER_UP_AND_DOWN)  || storyTags.includes(contentTags.EARLY_READER_ELKONIN_BOXES)  || storyTags.includes(contentTags.EARLY_READER_ELKONIN) || storyTags.includes(contentTags.EARLY_READER_CHANGE_ONE_PART));
}

export function isScorableEarlyReaderStory(story){
  if(!story) return false;

  const storyTags = story && story.tags;
  if (!storyTags) return false

  return (storyTags.includes(contentTags.EARLY_READER_WORD_LIST) || storyTags.includes(contentTags.EARLY_READER_REPETITIVE_TEXT)  || storyTags.includes(contentTags.EARLY_READER_LETTER_FLIES) || storyTags.includes(contentTags.EARLY_READER_UP_AND_DOWN)  || storyTags.includes(contentTags.EARLY_READER_ELKONIN)  || storyTags.includes(contentTags.EARLY_READER_CHANGE_ONE_PART));
}

export function isEarlyReaderActivity(activity){
  if(!activity){
    return false;
  }
  const tags = (activity && activity.story && activity.story.tags && activity.story.tags.length) ? activity.story.tags : [];
  return(tags.includes(contentTags.EARLY_READER_READ_WITH_ME) || tags.includes(contentTags.EARLY_READER_WORD_LIST) || tags.includes(contentTags.EARLY_READER_REPETITIVE_TEXT));
}

export function isEarlyReaderMode(state){
  return (state && (state.readWithMe || state.wordList || state.repetitiveText || state.changeOnePart || state.upAndDown || state.letterFlies || state.elkoninBoxes));
}

export async function flagNewEarlyReader(studentId,locale){
  //send only the flag for this locale: updateStudentMetadata merges against fresh server state,
  //so including the other locale's flag from the local store snapshot risks clobbering it (ASSMNT-2098)
  if(locale === 'en-us'){
    setUserData("isEarlyReader", true);
    await studentInformationService.updateStudentMetadata(studentId, {early_reader: true});
  }else{
    setUserData("isSpanishEarlyReader", true);
    await studentInformationService.updateStudentMetadata(studentId, {spanish_early_reader: true});
  }

  setUserData("startingEarlyReaderSequence", true);
}

export async function clearEarlyReader(studentId,locale='en-us'){
  if(locale === 'en-us'){
    setUserData("isEarlyReader", false);
    await studentInformationService.updateStudentMetadata(studentId, {early_reader: false});
  }else{
    setUserData("isSpanishEarlyReader", false);
    await studentInformationService.updateStudentMetadata(studentId, {spanish_early_reader: false});
  }
  setUserData("startingEarlyReaderSequence", false);
}

export default {
  getEarlyReaderMode,
  endOfEarlyReaderSequence,
  hasEarlyReaderSequenceInProgress,
  isEarlyReaderActivity,
  isEarlyReaderMode,
  flagNewEarlyReader,
  clearEarlyReader,
}
